import { Badge, Button, Card, Input, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, FileText, Headphones, Mic, Video } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  getPublicPage,
  getPublicSermon,
  listPublicSermons,
  type PublicSermon,
} from '../../lib/content-api'
import { ContentBody } from './markdown'

type Mode = { kind: 'page'; slug: string } | { kind: 'sermons' } | { kind: 'sermon'; slug: string }

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Public render preview. Lets staff enter their org slug and view published
 * content exactly as a visitor would (these endpoints are unauthenticated and
 * return only published rows). This mirrors the public website surface.
 */
export function ContentPublicView() {
  const [org, setOrg] = useState('')
  const [pageSlug, setPageSlug] = useState('')
  const [mode, setMode] = useState<Mode | null>(null)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted">
        <Link to="/app/content" className="inline-flex items-center gap-1 hover:text-fg">
          <ArrowLeft className="size-4" /> Content
        </Link>
      </div>

      <PageHeader
        title="Public preview"
        description="Render published content the way a visitor sees it."
      />

      <Card className="space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-fg">Church slug</span>
            <Input
              value={org}
              onChange={(e) => setOrg(e.target.value.trim())}
              placeholder="grace-community"
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-fg">Page slug</span>
            <Input
              value={pageSlug}
              onChange={(e) => setPageSlug(e.target.value.trim())}
              placeholder="about-our-church"
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={!org || !pageSlug}
            onClick={() => setMode({ kind: 'page', slug: pageSlug })}
          >
            <FileText />
            View page
          </Button>
          <Button variant="secondary" disabled={!org} onClick={() => setMode({ kind: 'sermons' })}>
            <Mic />
            View sermons
          </Button>
        </div>
      </Card>

      {mode?.kind === 'page' && <PublicPageView org={org} slug={mode.slug} />}
      {mode?.kind === 'sermons' && (
        <PublicSermonsView org={org} onOpen={(slug) => setMode({ kind: 'sermon', slug })} />
      )}
      {mode?.kind === 'sermon' && (
        <PublicSermonView org={org} slug={mode.slug} onBack={() => setMode({ kind: 'sermons' })} />
      )}
    </div>
  )
}

function StateWrap({
  isLoading,
  isError,
  error,
  children,
}: {
  isLoading: boolean
  isError: boolean
  error: unknown
  children: ReactNode
}) {
  if (isLoading)
    return (
      <Card className="flex items-center justify-center gap-2 py-16 text-muted">
        <Spinner /> Loading…
      </Card>
    )
  if (isError)
    return (
      <Card className="py-16 text-center text-sm text-danger">
        {error instanceof Error ? error.message : 'Not found or not published.'}
      </Card>
    )
  return <>{children}</>
}

function PublicPageView({ org, slug }: { org: string; slug: string }) {
  const q = useQuery({
    queryKey: ['content', 'public', 'page', org, slug],
    queryFn: () => getPublicPage(org, slug),
  })
  return (
    <StateWrap isLoading={q.isLoading} isError={q.isError} error={q.error}>
      {q.data && (
        <Card className="space-y-4 p-8">
          <h1 className="font-display text-3xl font-bold text-gradient">{q.data.title}</h1>
          {q.data.publishedAt && (
            <p className="text-xs text-dim">Published {formatDate(q.data.publishedAt)}</p>
          )}
          <ContentBody
            format={q.data.bodyFormat}
            body={q.data.body}
            className="space-y-3 text-base leading-relaxed text-fg"
          />
        </Card>
      )}
    </StateWrap>
  )
}

function PublicSermonsView({ org, onOpen }: { org: string; onOpen: (slug: string) => void }) {
  const q = useQuery({
    queryKey: ['content', 'public', 'sermons', org],
    queryFn: () => listPublicSermons(org),
  })
  return (
    <StateWrap isLoading={q.isLoading} isError={q.isError} error={q.error}>
      {q.data && q.data.length === 0 ? (
        <Card className="py-16 text-center text-sm text-muted">No published sermons yet.</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {q.data?.map((s) => (
            <Card key={s.slug} className="flex flex-col p-5">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                <div className="flex items-center gap-1 text-dim">
                  {s.audioUrl && <Headphones className="size-4" aria-label="Has audio" />}
                  {s.videoUrl && <Video className="size-4" aria-label="Has video" />}
                </div>
              </div>
              {s.series && (
                <Badge variant="secondary" className="mt-2 w-fit">
                  {s.series}
                </Badge>
              )}
              <p className="mt-2 text-xs text-dim">
                {[s.speaker, formatDate(s.preachedAt)].filter(Boolean).join(' · ')}
              </p>
              {s.summary && <p className="mt-2 line-clamp-3 text-sm text-muted">{s.summary}</p>}
              <Button variant="link" className="mt-3 w-fit" onClick={() => onOpen(s.slug)}>
                Read more
              </Button>
            </Card>
          ))}
        </div>
      )}
    </StateWrap>
  )
}

function PublicSermonView({
  org,
  slug,
  onBack,
}: {
  org: string
  slug: string
  onBack: () => void
}) {
  const q = useQuery({
    queryKey: ['content', 'public', 'sermon', org, slug],
    queryFn: () => getPublicSermon(org, slug),
  })
  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft />
        All sermons
      </Button>
      <StateWrap isLoading={q.isLoading} isError={q.isError} error={q.error}>
        {q.data && <SermonArticle sermon={q.data} />}
      </StateWrap>
    </div>
  )
}

function SermonArticle({ sermon }: { sermon: PublicSermon }) {
  return (
    <Card className="space-y-5 p-8">
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-bold text-gradient">{sermon.title}</h1>
        <p className="text-sm text-muted">
          {[sermon.speaker, sermon.scripture, formatDate(sermon.preachedAt)]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {sermon.series && <Badge variant="secondary">{sermon.series}</Badge>}
      </div>

      {sermon.videoUrl && (
        <a
          href={sermon.videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-secondary underline-offset-2 hover:underline"
        >
          <Video className="size-4" /> Watch video
        </a>
      )}
      {sermon.audioUrl && (
        <audio controls src={sermon.audioUrl} className="w-full">
          <track kind="captions" />
        </audio>
      )}

      {sermon.summary && <p className="text-base text-muted">{sermon.summary}</p>}

      {sermon.body && (
        <ContentBody
          format={sermon.bodyFormat}
          body={sermon.body}
          className="space-y-3 text-base leading-relaxed text-fg"
        />
      )}
    </Card>
  )
}
